import React from "react";
import styles from "./Resources.module.scss";
import Image from "next/image";
import GoToIcon from "./GoToIcon";
import EventSVG from "./EventSVG";
import { useRouter } from "next/router";
import BlogSVG from "./BlogSVG";

// Helper function to format date
const formatEventDate = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const EventTag = () => {
  return (
    <div className={`${styles.eventTag} ${styles.eventTagOverlay}`}>
      <EventSVG />
      <p>EVENTS</p>
    </div>
  );
};

const BlogTag = () => {
  return (
    <div className={`${styles.blogTag} ${styles.blogTagOverlay}`}>
      <BlogSVG />
      <p>BLOG</p>
    </div>
  );
};

// Skeleton components for loading states
const FeaturedEventSkeleton = () => {
  return (
    <div className={styles.featuredBlog}>
      <div
        className={`${styles.featuredBlogImage} ${styles.skeletonElement}`}
      />
      <div className={styles.informationSection}>
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonDescription} />
        <div className={styles.featuredBlogInfoRow}>
          <div className={styles.featuredBlogInfoItem}>
            <div className={styles.skeletonHeading} />
            <div className={styles.skeletonValueLocation} />
          </div>
          <div className={styles.featuredBlogInfoItem}>
            <div className={styles.skeletonHeading} />
            <div className={styles.skeletonValueDate} />
          </div>
        </div>
        <div className={styles.skeletonButton} />
      </div>
    </div>
  );
};

const EventSkeleton = () => {
  return (
    <div className={styles.blog}>
      <div className={`${styles.blogImage} ${styles.skeletonElement}`} />
      <div className={styles.blogHeadingDateRow}>
        <div className={styles.blogHeadingDate}>
          <div className={styles.skeletonBlogTitle} />
          <div className={styles.skeletonBlogDate} />
        </div>
        <div className={styles.skeletonGoToButton} />
      </div>
    </div>
  );
};

// Featured Blog Component
const FeaturedBlog = ({ blog }) => {
  const router = useRouter();
  if (!blog) return null;

  return (
    <div className={styles.featuredBlog}>
      {/* Image */}
      <div className={styles.imageWrapper}>
        {blog.image ? (
          <img
            className={styles.featuredBlogImage}
            src={blog.image}
            alt={blog.title}
            style={{ objectFit: "cover" }}
          />
        ) : (
          <div
            className={`${styles.featuredBlogImage} ${styles.imagePlaceholder}`}
          >
            No Image Available
          </div>
        )}
        <BlogTag />
      </div>
      {/* Information Section */}
      <div className={styles.informationSection}>
        {/* Title */}
        <div className={styles.featuredBlogTitle}>{blog.title}</div>
        {/* Description */}
        <div className={styles.featuredBlogDescription}>{blog.description}</div>
        {/* Row of Info items */}
        <div className={styles.featuredBlogInfoRow}>
          {/* Category Info Item */}
          <div className={styles.featuredBlogInfoItem}>
            <div className={styles.featuredBlogHeading}>Category</div>
            <div className={styles.featuredBlogValue}>
              {blog.categories?.name || "Blog"}
            </div>
          </div>
          {/* <div className={styles.featuredBlogInfoItem}>
            <div className={styles.featuredBlogHeading}>Type</div>
            <div className={styles.featuredBlogValue}>Blog Post</div>
          </div> */}
        </div>
        {/* Read Blog Button */}
        <button
          className={styles.readBlogButton}
          onClick={() =>
            router.push(`/blogs/${blog.categories.slug}/${blog.slug}`)
          }
        >
          Read Blog
        </button>
      </div>
    </div>
  );
};

// Featured Event Component
const FeaturedEvent = ({ event }) => {
  const router = useRouter();
  if (!event) return null;

  return (
    <div className={styles.featuredBlog}>
      {/* Image */}
      <div className={styles.imageWrapper}>
        {event.thumbnailImgUrl ? (
          <img
            className={styles.featuredBlogImage}
            src={event.thumbnailImgUrl}
            alt={event.title}
            style={{ objectFit: "cover" }}
          />
        ) : (
          <div
            className={`${styles.featuredBlogImage} ${styles.imagePlaceholder}`}
          >
            No Image Available
          </div>
        )}
        <EventTag />
      </div>
      {/* Information Section */}
      <div className={styles.informationSection}>
        {/* Title */}
        <div className={styles.featuredBlogTitle}>{event.title}</div>
        {/* Description */}
        <div className={styles.featuredBlogDescription}>
          {event.description}
        </div>
        {/* Row of Info items */}
        <div className={styles.featuredBlogInfoRow}>
          {/* Location Info Item */}
          <div className={styles.featuredBlogInfoItem}>
            <div className={styles.featuredBlogHeading}>Location</div>
            <div className={styles.featuredBlogValue}>
              {event.city}
              {event.city && event.country ? ", " : ""}
              {event.country}
            </div>
          </div>
          <div className={styles.featuredBlogInfoItem}>
            <div className={styles.featuredBlogHeading}>Event Date</div>
            <div className={styles.featuredBlogValue}>
              {formatEventDate(event.dateOfEvent)}
            </div>
          </div>
        </div>
        {/* View Event Button */}
        <button
          className={styles.readBlogButton}
          onClick={() => router.push(`/events/${event.slug}-${event.id}`)}
        >
          View Event
        </button>
      </div>
    </div>
  );
};

// Blog Component
const Blog = ({ blog }) => {
  const router = useRouter();
  if (!blog) return null;

  return (
    <div className={styles.blog}>
      {/* Image */}
      <div className={styles.imageWrapper}>
        {blog.image ? (
          <img src={blog.image} alt={blog.title} className={styles.blogImage} />
        ) : (
          <div className={`${styles.blogImage} ${styles.imagePlaceholder}`}>
            No Image Available
          </div>
        )}
        <BlogTag />
      </div>
      {/* Row of Heading + Date and Go to Button */}
      <div className={styles.blogHeadingDateRow}>
        {/* Heading and date */}
        <div className={styles.blogHeadingDate}>
          <div className={styles.blogHeading}>{blog.title}</div>
          <div className={styles.blogDate}>
            {blog.categories?.name || "Blog"}
          </div>
        </div>
        {/* Go to Button */}
        <button
          className={styles.goToButton}
          onClick={() =>
            router.push(`/blogs/${blog.categories.slug}/${blog.slug}`)
          }
        >
          <GoToIcon />
        </button>
      </div>
    </div>
  );
};

// Event Component
const Event = ({ event }) => {
  const router = useRouter();
  if (!event) return null;

  return (
    <div className={styles.blog}>
      {/* Image */}
      <div className={styles.imageWrapper}>
        {event.thumbnailImgUrl ? (
          <img
            src={event.thumbnailImgUrl}
            alt={event.title}
            className={styles.blogImage}
          />
        ) : (
          <div className={`${styles.blogImage} ${styles.imagePlaceholder}`}>
            No Image Available
          </div>
        )}
        <EventTag />
      </div>
      {/* Row of Heading + Date and Go to Button */}
      <div className={styles.blogHeadingDateRow}>
        {/* Heading and date */}
        <div className={styles.blogHeadingDate}>
          <div className={styles.blogHeading}>{event.title}</div>
          <div className={styles.blogDate}>
            {event.city}
            {event.city && event.country ? ", " : ""}
            {event.country}
          </div>
          <div className={styles.blogDate}>
            {formatEventDate(event.dateOfEvent)}
          </div>
        </div>
        {/* Go to Button */}
        <button
          className={styles.goToButton}
          onClick={() => router.push(`/events/${event.slug}-${event.id}`)}
        >
          <GoToIcon />
        </button>
      </div>
    </div>
  );
};

function Resources({
  newsletterData = { blogdata: [], eventdata: [] },
  error = null,
}) {
  const { blogdata, eventdata } = newsletterData;

  // Combine blog and event data into a single array, alternating between blogs and events
  const combinedContent = [];
  const maxLength = Math.max(blogdata.length, eventdata.length);

  for (let i = 0; i < maxLength; i++) {
    if (i < blogdata.length) {
      combinedContent.push({ ...blogdata[i], type: "blog" });
    }
    if (i < eventdata.length) {
      combinedContent.push({ ...eventdata[i], type: "event" });
    }
  }

  // Get featured content (first item) and remaining content (next 3 items)
  const featuredContent =
    combinedContent.length > 0 ? combinedContent[0] : null;
  const remainingContent = combinedContent.slice(1, 4);

  if (error) {
    return (
      <div className={styles.resources}>
        {/* Small Heading */}
        <h6>RESOURCES</h6>
        {/* Big Heading */}
        <h2>Blogs, Events & News at Elchemy</h2>

        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>Failed to load content: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resources}>
      {/* Small Heading */}
      <h6>RESOURCES</h6>
      {/* Big Heading */}
      <h2>Blogs, Events & News at Elchemy</h2>

      {/* Featured Content */}
      {featuredContent ? (
        featuredContent.type === "blog" ? (
          <FeaturedBlog blog={featuredContent} />
        ) : (
          <FeaturedEvent event={featuredContent} />
        )
      ) : (
        <FeaturedBlog blog={null} />
      )}

      {/* Row of Content */}
      <div className={styles.blogRow}>
        {remainingContent.length > 0 ? (
          <>
            {remainingContent.map((item, index) => {
              if (item.type === "blog") {
                return (
                  <Blog key={`blog-${item.wp_blog_id || index}`} blog={item} />
                );
              } else if (item.type === "event") {
                return <Event key={`event-${item.id}`} event={item} />;
              } else {
                return null;
              }
            })}
          </>
        ) : (
          <>
            <Blog blog={null} />
            <Blog blog={null} />
            <Blog blog={null} />
          </>
        )}
      </div>
    </div>
  );
}

export default Resources;
